import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import imageCompression from 'browser-image-compression';
import debounce from 'just-debounce-it';
import sortBy from 'just-sort-by';
import { useState } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';

import ColorPicker from '@/components/ColorPicker';
import { FormErrorMessage } from '@/components/FormErrorMessage';
import SimpleEditor from '@/components/TipTapEditorV3/SimpleEditor';
import { showErrorToast, showSuccessToast } from '@/components/ToastNotification';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from '@/components/ui/drawer';
import { ImageUploadButton } from '@/components/ui/image-upload-button';
import { Input } from '@/components/ui/input';
import { createDiscipline, updateDisciplines } from '@/hooks/mutations/disciplineMutations';
import { useIsBreakpoint } from '@/hooks/use-is-breakpoint';
import { CloseIcon } from '@/icons/index';
import type { DisciplineWithCourses } from '@/lib/types';
import { StyledHR } from '@/Shared/StyledElements';

interface DisciplineModalProps {
	triggerModal(): void;
	modalData: {
		isActive: boolean;
		isEditing: boolean;
		discipline: DisciplineWithCourses | null;
	};
	allDisciplines: DisciplineWithCourses[] | undefined;
}

interface FormValues {
	disciplineName: string;
	color: string;
	disciplineImage: File[];
}

const DisciplineListModal = ({ modalData, triggerModal, allDisciplines }: DisciplineModalProps) => {
	const { isEditing, discipline, isActive } = modalData;
	const [selectedColor, setSelectedColor] = useState(
		modalData.discipline?.color ?? 'var(--elevate-primary)'
	);
	const [isSaving, setIsSaving] = useState(false);
	const [imagePreview, setImagePreview] = useState<any>(discipline?.image);
	const {
		handleSubmit,
		register,
		formState: { errors },
		setValue,
		control,
		getValues,
	} = useForm<FormValues>();
	const [current, setCurrentPhilosophy] = useState(discipline?.philosophy);
	const queryClient = useQueryClient();
	const isMobile = useIsBreakpoint('max', 768);

	const { mutate: disciplineUpdateMutation, status: updateStatus } = useMutation({
		mutationKey: ['discipline-update-mutation'],
		mutationFn: updateDisciplines,
		onSuccess: (returnValue) => {
			showSuccessToast('Discipline Updated');
			setIsSaving(false);
			// update query cache
			const schoolId = import.meta.env.VITE_SCHOOL_ID;
			const globalSettingObj = queryClient.getQueryData(['get-settings-divisions-disciplines']) as
				| SettingsDivisionDiscipline
				| undefined;
			const previousDisciplineData = queryClient.getQueryData(['get-all-disciplines', schoolId]) as
				| DisciplineWithCourses[]
				| undefined;

			if (returnValue && previousDisciplineData) {
				const idx = previousDisciplineData.findIndex(
					(discipline) => discipline.id === returnValue.id
				);
				if (idx !== -1) {
					const clone = [...previousDisciplineData];
					clone[idx] = returnValue;
					queryClient.setQueryData(['get-all-disciplines', schoolId], clone);
					queryClient.invalidateQueries({
						queryKey: ['get-course-alignment'],
					});
					queryClient.invalidateQueries({
						queryKey: ['get-settings-divisions-disciplines'],
					});

					if (globalSettingObj) {
						queryClient.setQueryData(['get-settings-divisions-disciplines'], {
							...globalSettingObj,
							disciplines: clone,
						});
					}
				}
			}
		},
		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const { mutate: disciplineCreateMutation, status: createStatus } = useMutation({
		mutationFn: createDiscipline,
		onSuccess: (createdDiscipline: DisciplineWithCourses) => {
			showSuccessToast('Discipline Added');
			setIsSaving(false);
			// update query cache
			const schoolId = import.meta.env.VITE_SCHOOL_ID;
			const previousDisciplineData = queryClient.getQueryData(['get-all-disciplines', schoolId]) as
				| DisciplineWithCourses[]
				| undefined;

			if (previousDisciplineData) {
				const updatedCache = sortBy([createdDiscipline, ...previousDisciplineData], 'name');
				queryClient.setQueryData(['get-all-disciplines', schoolId], updatedCache);
			}
			queryClient.invalidateQueries({
				queryKey: ['get-settings-divisions-disciplines'],
			});
		},
		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const handleChange = debounce((html: string) => {
		setCurrentPhilosophy(html);
	}, 500);

	const blobToDataURL = (blob: Blob) => {
		return new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = () => reject(reader.error);
			reader.onabort = () => reject(new Error('Read aborted'));
			reader.readAsDataURL(blob);
		});
	};

	const getImageUrl = async (file: File) => {
		const options = {
			maxSizeMB: 1,
			maxWidthOrHeight: 1920,
			useWebWorker: true,
		};
		const compressedFile = await imageCompression(file, options);
		const dataUri = await blobToDataURL(compressedFile);

		try {
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cloudinary-image`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
					},
					body: JSON.stringify({
						action: 'upload',
						file: dataUri,
						folder: `elevate-${import.meta.env.VITE_CLOUDINARY_PRESET}`,
					}),
				}
			);

			if (!response.ok) {
				throw new Error('Upload failed');
			}

			const res = await response.json();
			const results = res.data?.secure_url.split('upload');
			const start = results[0];
			const end = results[1];
			return `${start}upload/c_scale,w_375${end}`;
		} catch {
			return 'error';
		}
	};

	const removePreviousImage = async () => {
		const imageRef = discipline?.image;
		const pattern = `(elevate-${
			import.meta.env.VITE_CLOUDINARY_PRESET
		}|elevate-sandbox)/(.*).(png|jpg|jpeg|avif)`;
		const regex = new RegExp(pattern);
		const imageId = imageRef?.match(regex)?.[2];

		fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cloudinary-image`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
			},
			body: JSON.stringify({
				action: 'delete',
				public_id: `elevate-${import.meta.env.VITE_CLOUDINARY_PRESET}/${imageId}`,
			}),
		}).catch((error) => {
			Sentry.captureException(error);
		});
	};

	const handleRemoveImage = () => {
		setImagePreview(null);
		removePreviousImage();
		setValue('disciplineImage', []);
		disciplineUpdateMutation({
			id: discipline?.id ?? '',
			philosophy: current ?? '',
			name: getValues('disciplineName').trim(),
			color: selectedColor,
			image: '',
			previousState: (discipline as any) ?? null,
		});
	};

	const onSubmit: SubmitHandler<FormValues> = async (formData) => {
		const file = formData.disciplineImage?.[0];

		let imageUrl = '';
		if (file) {
			setIsSaving(true);
			imageUrl = await getImageUrl(file);

			// If upload failed, show error and don't proceed
			if (imageUrl === 'error') {
				setIsSaving(false);
				showErrorToast('Failed to upload image. Please try again.');
				return;
			}

			removePreviousImage();
		}

		if (isEditing) {
			disciplineUpdateMutation({
				id: discipline?.id ?? '',
				philosophy: current ?? '',
				name: formData.disciplineName.trim(),
				color: formData.color,
				// Keep existing image if no new image was uploaded
				image: imageUrl || discipline?.image || '',
				previousState: (discipline as any) ?? null,
			});
			return;
		}
		disciplineCreateMutation({
			philosophy: current ?? '',
			name: formData.disciplineName.trim(),
			color: formData.color,
			image: imageUrl,
		});
	};

	const onPhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.preventDefault();
		const reader = new FileReader();
		const file = e?.target?.files?.[0];
		if (reader !== undefined && file !== undefined) {
			reader.onloadend = () => {
				setImagePreview(reader.result);
			};
			reader.readAsDataURL(file);
		}
	};

	// TODO: move to higher level utils file
	const isUnique = (value: string) => {
		const isPresent = allDisciplines?.some(
			(discipline) => discipline?.name?.toLowerCase() === value.toLowerCase()
		);
		return isPresent;
	};

	const renderButtonText = () => {
		if (isSaving) {
			return 'Saving...';
		}
		return isEditing ? 'Save' : 'Add Discipline';
	};

	const isPending = [createStatus, updateStatus].includes('pending');
	const title = isEditing ? `Editing: ${discipline?.name}` : 'Adding New Discipline';

	const handleOpenChange = (open: boolean) => {
		if (!open) triggerModal();
	};

	const formContent = (
		<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
			<div>
				<Input
					type="text"
					defaultValue={discipline?.name ?? ''}
					placeholder="Discipline Name"
					{...register('disciplineName', {
						validate: (value) => {
							if (value.trim() === '') {
								return 'Discipline Name is Required';
							}
							if (isEditing) {
								return true;
							}
							if (isUnique(value.trim())) {
								return 'This discipline already exists.';
							}
							return true;
						},
					})}
				/>
				<FormErrorMessage
					className="error-message"
					isShowing={!!errors?.disciplineName}
					message={errors.disciplineName?.message}
				/>
			</div>
			<Controller
				control={control}
				name="color"
				defaultValue={selectedColor}
				render={({ field }) => {
					return (
						<ColorPicker
							onChange={(color) => {
								setSelectedColor(field.value);
								field.onChange(color.hex);
							}}
							color={selectedColor}
						/>
					);
				}}
			/>
			{imagePreview && (
				<div className="inline-flex h-[175px] mb-6 mt-6 max-w-[250px] relative [&_img]:object-contain [&_button]:right-[-40px]">
					<CloseIcon passedEvent={handleRemoveImage} />
					<img src={imagePreview} alt="" />
				</div>
			)}
			{!imagePreview && (
				<div className="my-6 max-w-[250px] min-h-[175px]">
					<img
						className="object-contain w-full"
						src="https://placehold.co/300x200?text=No Image Selected"
						alt=""
					/>
				</div>
			)}
			<ImageUploadButton
				hasPreview={!!imagePreview}
				accept=".jpeg, .png, .jpg, .avif"
				{...register('disciplineImage')}
				onChange={onPhotoUpload}
			/>
			<StyledHR />
			<SimpleEditor
				content={current ?? ''}
				onChange={handleChange}
				placeholder="Add discipline philosophy..."
				isEditable
			/>
		</form>
	);

	const saveButton = (
		<Button
			isLoading={isPending}
			disabled={isPending || isSaving}
			type="submit"
			onClick={handleSubmit(onSubmit)}
		>
			{renderButtonText()}
		</Button>
	);

	const description = isEditing
		? "Update this discipline's name, color, image, and philosophy."
		: 'Add a name, color, image, and philosophy for the new discipline.';

	if (isMobile) {
		return (
			<Drawer open={isActive} onOpenChange={handleOpenChange}>
				<DrawerContent className="max-h-[85vh]">
					<DrawerHeader>
						<DrawerTitle className="pb-2">{title}</DrawerTitle>
						<DrawerDescription className="text-base">{description}</DrawerDescription>
					</DrawerHeader>
					<div className="px-4 pb-2 flex-1 overflow-y-auto">{formContent}</div>
					<DrawerFooter>{saveButton}</DrawerFooter>
				</DrawerContent>
			</Drawer>
		);
	}

	return (
		<Dialog open={isActive} onOpenChange={handleOpenChange}>
			<DialogContent className="max-w-[800px]">
				<DialogHeader>
					<DialogTitle className="pb-2">{title}</DialogTitle>
					<DialogDescription className="text-base">{description}</DialogDescription>
				</DialogHeader>
				<div className="max-h-[70vh] overflow-y-auto pr-1 mb-4">{formContent}</div>
				<div className="flex justify-end">{saveButton}</div>
			</DialogContent>
		</Dialog>
	);
};

export default DisciplineListModal;
